import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  ChevronLeftIcon, CheckCircleIcon, ArrowPathIcon, BanknotesIcon,
  ClockIcon, ExclamationTriangleIcon, CalendarDaysIcon, ArrowDownTrayIcon,
  LockClosedIcon, LockOpenIcon, PencilSquareIcon, XMarkIcon, PlusCircleIcon,
  GiftIcon,
} from '@heroicons/react/24/outline';

const style = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .tm-root * { box-sizing:border-box; }
  .tm-root { font-family:'Sora',sans-serif; color:#1a1d2e; animation:tmFade .4s ease both; }
  @keyframes tmFade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }

  .tm-back { display:inline-flex; align-items:center; gap:6px; font-size:.78rem; font-weight:600; color:#7880a4; cursor:pointer; margin-bottom:1.4rem; border:none; background:none; font-family:'Sora',sans-serif; padding:0; transition:color .15s; }
  .tm-back:hover { color:#3b6ef8; }
  .tm-back svg { width:14px; height:14px; stroke-width:2.5; }

  .tm-header { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:1.5rem; flex-wrap:wrap; gap:1rem; }
  .tm-title-wrap { display:flex; align-items:center; gap:12px; }
  .tm-avatar { width:46px; height:46px; border-radius:13px; background:#ecfdf5; color:#10c98e; display:flex; align-items:center; justify-content:center; font-size:.95rem; font-weight:700; text-transform:uppercase; flex-shrink:0; }
  .tm-name  { font-size:1.25rem; font-weight:700; letter-spacing:-.02em; color:#0f1123; }
  .tm-month { font-size:.75rem; color:#9094a8; margin-top:2px; }
  .tm-actions { display:flex; gap:8px; flex-wrap:wrap; align-items:center; }

  .btn { display:inline-flex; align-items:center; gap:7px; padding:9px 16px; border:none; border-radius:10px; font-family:'Sora',sans-serif; font-size:.8rem; font-weight:600; cursor:pointer; transition:all .18s; }
  .btn svg { width:15px; height:15px; stroke-width:2; }
  .btn-blue  { background:#3b6ef8; color:white; }
  .btn-blue:hover  { background:#2d5ef0; box-shadow:0 4px 14px rgba(59,110,248,.35); transform:translateY(-1px); }
  .btn-green { background:#10c98e; color:white; }
  .btn-green:hover { background:#0db87e; }
  .btn-red   { background:#fef2f2; color:#ef4444; border:1px solid #fecaca; }
  .btn-red:hover   { background:#ef4444; color:white; }
  .btn-ghost { background:#f0f2f7; color:#6b7194; }
  .btn-ghost:hover { background:#e4e7f0; }
  .btn:disabled { opacity:.5; cursor:not-allowed; transform:none !important; box-shadow:none !important; }

  /* Export dropdown */
  .export-wrap { position:relative; display:inline-flex; }
  .export-menu { position:absolute; top:calc(100% + 6px); right:0; background:white; border:1px solid #e8ebf4; border-radius:12px; box-shadow:0 8px 24px rgba(0,0,0,.1); min-width:160px; z-index:100; overflow:hidden; }
  .export-menu-item { display:flex; align-items:center; gap:8px; padding:9px 14px; font-family:'Sora',sans-serif; font-size:.78rem; font-weight:600; color:#1a1d2e; cursor:pointer; border:none; background:none; width:100%; text-align:left; transition:background .12s; }
  .export-menu-item:hover { background:#f5f7ff; color:#3b6ef8; }
  .export-menu-item svg { width:14px; height:14px; stroke-width:2; color:#9094a8; }
  .export-menu-sep   { height:1px; background:#f0f2f7; margin:4px 0; }
  .export-menu-label { font-size:.65rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.07em; padding:6px 14px 2px; }

  .finalized-banner { display:flex; align-items:center; gap:10px; padding:12px 16px; border-radius:12px; margin-bottom:1.25rem; font-size:.8rem; font-weight:600; flex-wrap:wrap; }
  .finalized-banner.green { background:#ecfdf5; color:#065f46; border:1px solid #a7f3d0; }
  .finalized-banner.blue  { background:#eef2ff; color:#3730a3; border:1px solid #c7d2fe; }
  .finalized-banner svg   { width:16px; height:16px; stroke-width:2; flex-shrink:0; }
  .fb-meta { font-size:.7rem; font-weight:400; color:#6b7194; margin-top:1px; }

  /* 6-column stat grid */
  .stat-grid { display:grid; grid-template-columns:repeat(6,1fr); gap:.9rem; margin-bottom:1.4rem; }
  .stat-card { background:white; border-radius:14px; padding:1rem 1.1rem; border:1px solid #e8ebf4; position:relative; overflow:hidden; animation:tmFade .4s ease both; }
  .stat-card::after { content:''; position:absolute; top:0; right:0; width:60px; height:60px; border-radius:0 14px 0 60px; opacity:.06; }
  .stat-card.blue::after{background:#3b6ef8}   .stat-card.green::after{background:#10c98e}
  .stat-card.amber::after{background:#f59e0b}  .stat-card.red::after{background:#ef4444}
  .stat-card.purple::after{background:#8b5cf6}
  .s-icon { width:32px; height:32px; border-radius:9px; display:flex; align-items:center; justify-content:center; margin-bottom:.7rem; }
  .s-icon svg { width:15px; height:15px; stroke-width:1.8; }
  .s-icon.blue{background:#eef2ff;color:#3b6ef8}   .s-icon.green{background:#ecfdf5;color:#10c98e}
  .s-icon.amber{background:#fffbeb;color:#f59e0b}  .s-icon.red{background:#fef2f2;color:#ef4444}
  .s-icon.purple{background:#f5f3ff;color:#8b5cf6}
  .s-label { font-size:.68rem; font-weight:600; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; margin-bottom:2px; }
  .s-value { font-size:1.1rem; font-weight:700; letter-spacing:-.02em; color:#0f1123; line-height:1; font-family:'JetBrains Mono',monospace; }
  .s-sub   { font-size:.68rem; color:#b0b5cc; margin-top:3px; }

  .table-panel { background:white; border-radius:16px; border:1px solid #e8ebf4; overflow:hidden; animation:tmFade .45s ease both; animation-delay:.2s; margin-bottom:1.4rem; }
  .table-panel-header { display:flex; align-items:center; justify-content:space-between; padding:14px 18px; border-bottom:1px solid #f0f2f8; flex-wrap:wrap; gap:8px; }
  .tp-title { font-size:.86rem; font-weight:600; color:#1a1d2e; }
  .tp-sub   { font-size:.7rem; color:#9094a8; margin-top:1px; }

  .day-table { width:100%; border-collapse:collapse; }
  .day-table thead { background:#fafbff; border-bottom:1px solid #edf0f9; }
  .day-table th { padding:10px 14px; text-align:center !important; font-size:.65rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.07em; white-space:nowrap; }
  .day-table th.right { text-align:right; }
  .day-table tbody tr { border-bottom:1px solid #f3f5fb; transition:background .12s; }
  .day-table tbody tr:last-child { border-bottom:none; }
  .day-table tbody tr:hover { background:#fafbff; }
  .day-table td { padding:10px 14px; vertical-align:middle; font-size:.79rem; text-align:center !important; }
  .day-table td.right { text-align:right; }
  .day-date { font-family:'JetBrains Mono',monospace; font-size:.78rem; font-weight:500; color:#1a1d2e; }
  .day-dow  { font-size:.67rem; color:#9094a8; margin-top:1px; }
  .total-row td { background:#fafbff; font-weight:700; border-top:2px solid #e8ebf4 !important; text-align:center !important; }

  .day-cards { display:none; padding:10px; }
  .day-card  { background:#fafbff; border-radius:12px; border:1px solid #e8ebf4; padding:12px 13px; margin-bottom:8px; }
  .dc-top    { display:flex; align-items:center; justify-content:space-between; margin-bottom:8px; gap:8px; }
  .dc-date   { font-family:'JetBrains Mono',monospace; font-size:.8rem; font-weight:600; color:#0f1123; }
  .dc-dow    { font-size:.67rem; color:#9094a8; }
  .dc-row    { display:flex; align-items:center; justify-content:space-between; font-size:.75rem; padding:3px 0; border-bottom:1px solid #f0f2f8; gap:8px; }
  .dc-row:last-of-type { border-bottom:none; }
  .dc-label  { color:#9094a8; font-weight:600; white-space:nowrap; }
  .dc-net    { font-family:'JetBrains Mono',monospace; font-size:.9rem; font-weight:700; color:#10c98e; }

  .day-total-card { background:white; border-radius:12px; border:2px solid #e8ebf4; padding:13px; margin:0 10px 10px; }
  .dtc-row { display:flex; justify-content:space-between; font-size:.76rem; padding:4px 0; border-bottom:1px solid #f3f5fb; gap:8px; }
  .dtc-row:last-child { border-bottom:none; }
  .dtc-label { color:#9094a8; font-weight:600; }

  .status-pill { display:inline-flex; align-items:center; font-size:.67rem; font-weight:600; padding:2px 9px; border-radius:99px; }
  .status-pill.present  { background:#ecfdf5; color:#10c98e; }
  .status-pill.absent   { background:#fef2f2; color:#ef4444; }
  .status-pill.on-leave { background:#eef2ff; color:#3b6ef8; }
  .status-pill.holiday  { background:#f3f0ff; color:#8b5cf6; }
  .leave-type-pill { display:inline-flex; font-size:.63rem; font-weight:600; padding:1px 6px; border-radius:99px; margin-left:4px; }
  .leave-type-pill.paid   { background:#ecfdf5; color:#10c98e; }
  .leave-type-pill.unpaid { background:#fef2f2; color:#ef4444; }
  .mono-sm  { font-family:'JetBrains Mono',monospace; font-size:.75rem; color:#6b7194; }
  .ot-badge { display:inline-flex; align-items:center; font-family:'JetBrains Mono',monospace; font-size:.68rem; font-weight:600; padding:2px 7px; border-radius:99px; background:#f3f0ff; color:#8b5cf6; }
  .amt        { font-family:'JetBrains Mono',monospace; font-size:.78rem; font-weight:600; color:#1a1d2e; }
  .amt.red    { color:#ef4444; }
  .amt.green  { color:#10c98e; font-size:.82rem; }
  .amt.dim    { color:#c0c5dc; font-weight:400; }
  .amt.purple { color:#8b5cf6; }
  .amt.teal   { color:#06b6d4; }

  .edit-btn { width:28px; height:28px; border-radius:7px; border:1px solid #e4e7f0; background:white; color:#9094a8; cursor:pointer; display:flex; align-items:center; justify-content:center; transition:all .15s; }
  .edit-btn:hover { background:#eef2ff; border-color:#3b6ef8; color:#3b6ef8; }
  .edit-btn svg { width:13px; height:13px; stroke-width:2; }

  .tm-empty { text-align:center; padding:3.5rem 2rem; }
  .tm-empty-icon  { width:48px; height:48px; border-radius:13px; background:#eef2ff; color:#3b6ef8; display:flex; align-items:center; justify-content:center; margin:0 auto 12px; }
  .tm-empty-icon svg { width:22px; height:22px; stroke-width:1.6; }
  .tm-empty-title { font-size:.9rem; font-weight:600; color:#1a1d2e; margin-bottom:4px; }
  .tm-empty-sub   { font-size:.76rem; color:#9094a8; }
  .tm-loading { display:flex; align-items:center; justify-content:center; height:200px; gap:10px; color:#9094a8; font-size:.88rem; }
  .spinner { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:#10c98e; border-radius:50%; animation:spin .7s linear infinite; }
  @keyframes spin { to{transform:rotate(360deg);} }

  .modal-backdrop { position:fixed; inset:0; background:rgba(15,17,35,.55); backdrop-filter:blur(4px); display:flex; align-items:center; justify-content:center; z-index:1000; animation:tmFade .2s ease; padding:16px; overflow-y:auto; }
  .modal { background:white; border-radius:20px; padding:26px; width:100%; box-shadow:0 20px 60px rgba(0,0,0,.2); animation:modalIn .25s ease; margin:auto; max-height:90vh; overflow-y:auto; }
  .modal-sm { max-width:380px; }
  .modal-lg { max-width:560px; }
  @keyframes modalIn { from{opacity:0;transform:scale(.94)} to{opacity:1;transform:scale(1)} }
  .modal-head  { display:flex; align-items:center; justify-content:space-between; margin-bottom:18px; gap:8px; }
  .modal-title { font-size:1rem; font-weight:700; color:#0f1123; }
  .modal-close { width:30px; height:30px; border-radius:8px; border:none; background:#f0f2f7; color:#6b7194; cursor:pointer; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
  .modal-close:hover { background:#e4e7f0; }
  .modal-close svg { width:15px; height:15px; stroke-width:2; }
  .modal-sub  { font-size:.8rem; color:#6b7194; line-height:1.55; margin-bottom:20px; margin-top:-10px; }
  .modal-btns { display:flex; gap:8px; justify-content:flex-end; margin-top:20px; flex-wrap:wrap; }

  .edit-grid { display:grid; grid-template-columns:1fr 1fr; gap:14px; }
  .edit-field { display:flex; flex-direction:column; gap:5px; }
  .edit-field.span2 { grid-column:1/-1; }
  .edit-label { font-size:.68rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .edit-input,.edit-select { font-family:'Sora',sans-serif; font-size:.82rem; padding:9px 11px; border:1.5px solid #e0e4f0; border-radius:9px; background:#f8f9fc; color:#1a1d2e; outline:none; transition:border-color .15s,box-shadow .15s; width:100%; }
  .edit-input:focus,.edit-select:focus { border-color:#3b6ef8; background:white; box-shadow:0 0 0 3px rgba(59,110,248,.08); }
  .edit-input.mono { font-family:'JetBrains Mono',monospace; }
  .edit-hint { font-size:.67rem; color:#b0b5cc; }

  .preview-box { background:#f8f9fc; border:1px solid #e8ebf4; border-radius:12px; padding:14px 16px; margin-top:4px; }
  .preview-title { font-size:.68rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; margin-bottom:10px; }
  .preview-row { display:flex; justify-content:space-between; align-items:center; padding:4px 0; border-bottom:1px solid #f0f2f8; font-size:.78rem; }
  .preview-row:last-child { border-bottom:none; padding-bottom:0; }
  .preview-row.total { padding-top:8px; font-weight:700; font-size:.84rem; }
  .preview-key { color:#6b7194; }
  .preview-val { font-family:'JetBrains Mono',monospace; font-weight:600; color:#1a1d2e; }
  .preview-val.green  { color:#10c98e; }
  .preview-val.red    { color:#ef4444; }
  .preview-val.purple { color:#8b5cf6; }
  .preview-val.teal   { color:#06b6d4; }

  @media (max-width:1200px) { .stat-grid { grid-template-columns:repeat(3,1fr); } }
  @media (max-width:768px) {
    .tm-name { font-size:1.05rem; }
    .stat-grid { grid-template-columns:repeat(2,1fr); }
    .day-table-wrap { display:none !important; }
    .day-cards { display:block; }
    .modal { padding:18px; }
    .edit-grid { grid-template-columns:1fr; }
    .edit-field.span2 { grid-column:1; }
    .modal-btns { justify-content:stretch; }
    .modal-btns .btn { flex:1; justify-content:center; }
  }
  @media (max-width:480px) { .stat-grid { grid-template-columns:1fr 1fr; } }
`;

const initials   = (n='') => n.split(' ').slice(0,2).map(w=>w[0]).join('').toUpperCase() || '?';
const fmt        = (n)    => `Rs. ${(n||0).toLocaleString('en-PK',{minimumFractionDigits:2,maximumFractionDigits:2})}`;
const fmtTime    = (d)    => d ? new Date(d).toLocaleTimeString('en-US',{hour:'2-digit',minute:'2-digit',hour12:true}) : '—';
const toTimeInput= (d)    => { if(!d) return ''; const dt=new Date(d); return `${String(dt.getHours()).padStart(2,'0')}:${String(dt.getMinutes()).padStart(2,'0')}`; };
const fmtDate    = (d)    => new Date(d).toLocaleDateString('en-US',{day:'numeric',month:'short'});
const dayOfWeek  = (d)    => new Date(d).toLocaleDateString('en-US',{weekday:'short'});
const monthLabel = (m)    => { const[y,mn]=m.split('-').map(Number); return new Date(y,mn-1,1).toLocaleDateString('en-US',{month:'long',year:'numeric'}); };

const previewCalc = (form, rule) => {
  const hourlyRate         = rule?.hourlyRate || 0;
  const workingHoursPerDay = rule?.workingHoursPerDay || 8;
  const overtimeRate       = rule?.overtimeRate || 1.5;
  let workHours = parseFloat(form.workHours) || 0;
  if (form.checkIn && form.checkOut) {
    const [ih,im] = form.checkIn.split(':').map(Number);
    const [oh,om] = form.checkOut.split(':').map(Number);
    const diff = (oh*60+om)-(ih*60+im);
    if (diff > 0) workHours = diff/60;
  }
  const regularHours  = Math.min(workHours, workingHoursPerDay);
  const overtimeHours = Math.max(0, workHours - workingHoursPerDay);
  const regularPay    = regularHours * hourlyRate;
  const overtimePay   = overtimeHours * hourlyRate * overtimeRate;
  const latePenalty   = (parseFloat(form.lateMinutes)||0) * (rule?.latePenaltyPerMinute||0);
  const earlyPenalty  = (parseFloat(form.earlyExitMinutes)||0) * (rule?.earlyExitPenaltyPerMinute||0);
  const deductions    = latePenalty + earlyPenalty;
  const netDay        = regularPay + overtimePay - deductions;
  return { workHours, regularHours, overtimeHours, regularPay, overtimePay, deductions, netDay };
};

/* ── Export helpers ── */
const exportCSV = (rows, filename) => {
  if (!rows.length) return;
  const hdrs = Object.keys(rows[0]);
  const esc  = v => { const s=v==null?'':String(v); return s.includes(',')||s.includes('"')?'"'+s.replace(/"/g,'""')+'"':s; };
  const csv  = [hdrs.join(','), ...rows.map(r=>hdrs.map(h=>esc(r[h])).join(','))].join('\n');
  const blob = new Blob([csv], {type:'text/csv;charset=utf-8;'});
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a'); a.href=url; a.download=filename+'.csv'; a.click();
  URL.revokeObjectURL(url);
};
const exportXLSX = async (rows, filename) => {
  try { const X=await import('xlsx'); const ws=X.utils.json_to_sheet(rows); const wb=X.utils.book_new(); X.utils.book_append_sheet(wb,ws,'Sheet1'); X.writeFile(wb,filename+'.xlsx'); }
  catch { exportCSV(rows, filename); }
};

const TeacherSalaryMonth = () => {
  const { teacherId, month } = useParams();
  const navigate = useNavigate();

  const [data,       setData]       = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [finalizing, setFinalizing] = useState(false);
  const [saving,     setSaving]     = useState(false);
  const [confirm,    setConfirm]    = useState(null);
  const [editDay,    setEditDay]    = useState(null);
  const [editForm,   setEditForm]   = useState({});
  const [addOpen,    setAddOpen]    = useState(false);
  const [addForm,    setAddForm]    = useState({ date:'', checkIn:'', checkOut:'', lateMinutes:'0', earlyExitMinutes:'0', status:'present' });
  const [adding,     setAdding]     = useState(false);
  const [exportOpen, setExportOpen] = useState(false);

  useEffect(() => { fetchDetail(); }, [teacherId, month]);

  useEffect(() => {
    if (!exportOpen) return;
    const h = () => setExportOpen(false);
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [exportOpen]);

  const fetchDetail = async () => {
    try { setLoading(true); const { data:res } = await api.get(`/admin/salary/teacher/${teacherId}/month/${month}`); setData(res); }
    catch { toast.error('Failed to load month detail'); } finally { setLoading(false); }
  };

  const handleAddSave = async () => {
    if (!addForm.date) { toast.error('Please select a date'); return; }
    try {
      setAdding(true);
      await api.post('/admin/attendance/record', { teacherId, date:addForm.date, checkIn:addForm.checkIn||undefined, checkOut:addForm.checkOut||undefined, lateMinutes:addForm.lateMinutes, earlyExitMinutes:addForm.earlyExitMinutes, status:addForm.status });
      toast.success('Attendance record created');
      setAddOpen(false); setAddForm({ date:'', checkIn:'', checkOut:'', lateMinutes:'0', earlyExitMinutes:'0', status:'present' }); fetchDetail();
    } catch(err) { toast.error(err.response?.data?.message||'Failed to create record'); }
    finally { setAdding(false); }
  };

  const openEdit = (day) => {
    setEditDay(day);
    setEditForm({ status:day.status, checkIn:toTimeInput(day.checkIn), checkOut:toTimeInput(day.checkOut), lateMinutes:String(day.lateMinutes||0), earlyExitMinutes:String(day.earlyMinutes||0), workHours:String(day.workHours||0), incentive:String(day.incentive||0) });
  };

  const handleEditSave = async () => {
    if (!editDay?.attendanceId) { toast.error('No attendance ID'); return; }
    try {
      setSaving(true);
      await api.put(`/admin/attendance/record/${editDay.attendanceId}`, { status:editForm.status, checkIn:editForm.checkIn||undefined, checkOut:editForm.checkOut||undefined, lateMinutes:editForm.lateMinutes, earlyExitMinutes:editForm.earlyExitMinutes, incentive:editForm.incentive });
      toast.success('Attendance updated'); setEditDay(null); fetchDetail();
    } catch { toast.error('Failed to save changes'); } finally { setSaving(false); }
  };

  const handleFinalize = async () => {
    try { setFinalizing(true); await api.post(`/admin/salary/teacher/${teacherId}/month/${month}/finalize`); toast.success('Salary finalized'); fetchDetail(); }
    catch { toast.error('Finalization failed'); } finally { setFinalizing(false); setConfirm(null); }
  };

  const handleStatusChange = async (newStatus) => {
    try { await api.put(`/admin/salary/record/${data.savedRecord._id}/status`, { status:newStatus }); toast.success(`Marked as ${newStatus}`); fetchDetail(); }
    catch { toast.error('Status update failed'); } finally { setConfirm(null); }
  };

  /* ── Build export rows ── */
  const buildDailyRows = () => (data?.days||[]).map(d => ({
    Date:              fmtDate(d.date),
    Day:               dayOfWeek(d.date),
    Status:            d.status,
    'Check In':        fmtTime(d.checkIn),
    'Check Out':       fmtTime(d.checkOut),
    'Work Hrs':        d.workHours?.toFixed(2),
    'Regular Hrs':     d.regularHours?.toFixed(2),
    'Overtime Hrs':    d.overtimeHours?.toFixed(2),
    'Late Min':        d.lateMinutes,
    'Early Exit Min':  d.earlyMinutes,
    'Incentive':       (d.incentive||0).toFixed(2),
    'Regular Pay':     d.regularPay?.toFixed(2),
    'OT Pay':          d.overtimePay?.toFixed(2),
    'Deductions':      d.deductions?.toFixed(2),
    'Net Day':         d.netDay?.toFixed(2),
  }));

  const buildSummaryRow = () => {
    if (!data) return [];
    const t = data.totals;
    return [{
      Name:            data.teacher?.name||'',
      'Employee ID':   data.teacher?.employeeId||'',
      Month:           month,
      'Days Present':  t.daysPresent,
      'Days Absent':   t.daysAbsent,
      'Days On Leave': t.daysOnLeave||0,
      'Total Hrs':     t.totalHours,
      'OT Hrs':        t.totalOvertimeHours,
      'Late Min':      t.totalLateMinutes,
      'Early Exit Min':t.totalEarlyMinutes,
      'Incentives':    (totalIncentives||0).toFixed(2),
      'Regular Pay':   (t.regularPay||0).toFixed(2),
      'OT Pay':        (t.overtimePay||0).toFixed(2),
      'Deductions':    (t.deductions||0).toFixed(2),
      'Net Salary':    (t.netSalary||0).toFixed(2),
      'Status':        data.savedRecord?.status||'not finalized',
    }];
  };

  const handleExport = async (scope, fmt_) => {
    setExportOpen(false);
    const name = data?.teacher?.name?.replace(/\s+/g,'-')||'teacher';
    if (scope === 'daily') {
      const rows = buildDailyRows();
      if (!rows.length) { toast.error('No data to export'); return; }
      fmt_==='csv' ? exportCSV(rows,`salary-daily-${name}-${month}`) : await exportXLSX(rows,`salary-daily-${name}-${month}`);
    } else {
      const rows = buildSummaryRow();
      fmt_==='csv' ? exportCSV(rows,`salary-summary-${name}-${month}`) : await exportXLSX(rows,`salary-summary-${name}-${month}`);
    }
  };

  if (loading) return <div className="tm-root"><style>{style}</style><div className="tm-loading"><div className="spinner"/>Loading…</div></div>;
  if (!data)   return null;

  const { teacher, days, totals, savedRecord } = data;
  const isFinalized     = !!savedRecord;
  const preview         = editDay ? previewCalc(editForm, editDay.ruleSnapshot) : null;
  const totalIncentives = totals.incentives ?? days.reduce((s,d) => s+(d.incentive||0), 0);

  return (
    <div className="tm-root"><style>{style}</style>

      <button className="tm-back" onClick={() => navigate(`/salary-records/${teacherId}`)}>
        <ChevronLeftIcon />Back to Months
      </button>

      <div className="tm-header">
        <div className="tm-title-wrap">
          <div className="tm-avatar">{initials(teacher?.name)}</div>
          <div>
            <div className="tm-name">{teacher?.name}</div>
            <div className="tm-month">{monthLabel(month)}{teacher?.employeeId ? ` · ${teacher.employeeId}` : ''}</div>
          </div>
        </div>
        <div className="tm-actions">

          {/* Export dropdown */}
          <div className="export-wrap" onMouseDown={e => e.stopPropagation()}>
            <button className="btn btn-ghost" onClick={() => setExportOpen(o => !o)}>
              <ArrowDownTrayIcon />Export
            </button>
            {exportOpen && (
              <div className="export-menu">
                <div className="export-menu-label">Day-by-Day</div>
                <button className="export-menu-item" onClick={() => handleExport('daily','csv')}><ArrowDownTrayIcon />CSV</button>
                <button className="export-menu-item" onClick={() => handleExport('daily','xlsx')}><ArrowDownTrayIcon />XLSX</button>
                <div className="export-menu-sep"/>
                <div className="export-menu-label">Monthly Summary</div>
                <button className="export-menu-item" onClick={() => handleExport('summary','csv')}><ArrowDownTrayIcon />CSV</button>
                <button className="export-menu-item" onClick={() => handleExport('summary','xlsx')}><ArrowDownTrayIcon />XLSX</button>
              </div>
            )}
          </div>

          <button className="btn btn-green" onClick={() => setAddOpen(true)}><PlusCircleIcon />Add Day</button>

          {isFinalized ? (
            <>
              {savedRecord.status==='unpaid'
                ? <button className="btn btn-green" onClick={() => setConfirm({type:'status',newStatus:'paid'})}><CheckCircleIcon />Mark Paid</button>
                : <button className="btn btn-red"   onClick={() => setConfirm({type:'status',newStatus:'unpaid'})}><LockOpenIcon />Mark Unpaid</button>}
              <button className="btn btn-blue" onClick={() => setConfirm({type:'finalize'})} disabled={finalizing}><ArrowPathIcon />Re-finalize</button>
            </>
          ) : (
            <button className="btn btn-blue" onClick={() => setConfirm({type:'finalize'})} disabled={finalizing}><LockClosedIcon />Finalize</button>
          )}
        </div>
      </div>

      {isFinalized && (
        <div className={`finalized-banner ${savedRecord.status==='paid' ? 'blue' : 'green'}`}>
          {savedRecord.status==='paid' ? <CheckCircleIcon/> : <LockClosedIcon/>}
          <div>
            <div>{savedRecord.status==='paid' ? `Paid on ${fmtDate(savedRecord.paidAt)} by ${savedRecord.paidBy}` : 'Finalized — awaiting payment'}</div>
            <div className="fb-meta">Finalized on {fmtDate(savedRecord.finalizedAt)} by {savedRecord.finalizedBy}</div>
          </div>
        </div>
      )}

      {/* ── 6-card stat grid ── */}
      <div className="stat-grid">
        <div className="stat-card blue"   style={{animationDelay:'.00s'}}><div className="s-icon blue"><CalendarDaysIcon/></div><div className="s-label">Present</div><div className="s-value">{totals.daysPresent}</div><div className="s-sub">{totals.daysAbsent} absent · {totals.daysOnLeave||0} leave</div></div>
        <div className="stat-card green"  style={{animationDelay:'.05s'}}><div className="s-icon green"><ClockIcon/></div><div className="s-label">Total Hours</div><div className="s-value">{totals.totalHours}</div><div className="s-sub">{totals.totalOvertimeHours} overtime</div></div>
        <div className="stat-card amber"  style={{animationDelay:'.10s'}}><div className="s-icon amber"><ExclamationTriangleIcon/></div><div className="s-label">Late Minutes</div><div className="s-value">{totals.totalLateMinutes}</div><div className="s-sub">{totals.totalEarlyMinutes} early exit</div></div>
        {/* Incentives */}
        <div className="stat-card purple" style={{animationDelay:'.15s'}}><div className="s-icon purple"><GiftIcon/></div><div className="s-label">Incentives</div><div className="s-value" style={{fontSize:'.9rem',color:'#8b5cf6'}}>{fmt(totalIncentives)}</div><div className="s-sub">bonuses added</div></div>
        <div className="stat-card red"    style={{animationDelay:'.20s'}}><div className="s-icon red"><BanknotesIcon/></div><div className="s-label">Deductions</div><div className="s-value" style={{fontSize:'.9rem'}}>{fmt(totals.deductions)}</div><div className="s-sub">penalties</div></div>
        <div className="stat-card green"  style={{animationDelay:'.25s'}}><div className="s-icon green"><BanknotesIcon/></div><div className="s-label">Net Salary</div><div className="s-value" style={{fontSize:'.9rem',color:'#10c98e'}}>{fmt(totals.netSalary)}</div><div className="s-sub">this month</div></div>
      </div>

      <div className="table-panel">
        <div className="table-panel-header">
          <div><div className="tp-title">Day-by-Day Breakdown</div><div className="tp-sub">{days.length} records</div></div>
        </div>

        {days.length === 0 ? (
          <div className="tm-empty"><div className="tm-empty-icon"><CalendarDaysIcon/></div><div className="tm-empty-title">No attendance records</div><div className="tm-empty-sub">No check-ins found for this month.</div></div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="day-table-wrap" style={{overflowX:'auto'}}>
              <table className="day-table">
                <thead><tr>
                  <th>Date</th><th>Status</th><th>Check In</th><th>Check Out</th>
                  <th>Hours</th><th>Overtime</th><th>Late</th><th>Early Exit</th>
                  <th>Regular Pay</th><th>OT Pay</th>
                  <th style={{color:'#8b5cf6'}}>Incentive</th>
                  <th>Deductions</th><th className="right">Net</th><th></th>
                </tr></thead>
                <tbody>
                  {days.map((d,i) => (
                    <tr key={i}>
                      <td><div className="day-date">{fmtDate(d.date)}</div><div className="day-dow">{dayOfWeek(d.date)}</div></td>
                      <td><span className={`status-pill ${d.status}`}>{d.status}</span>{d.status==='on-leave'&&d.leaveType&&<span className={`leave-type-pill ${d.leaveType}`}>{d.leaveType}</span>}</td>
                      <td><span className="mono-sm">{fmtTime(d.checkIn)}</span></td>
                      <td><span className="mono-sm">{fmtTime(d.checkOut)}</span></td>
                      <td><span className="mono-sm">{d.workHours?.toFixed(2)} hrs</span></td>
                      <td>{d.overtimeHours>0?<span className="ot-badge">+{d.overtimeHours?.toFixed(2)}</span>:<span className="amt dim">—</span>}</td>
                      <td><span className="mono-sm" style={{color:d.lateMinutes>0?'#f59e0b':'#b0b5cc'}}>{d.lateMinutes>0?`${d.lateMinutes}m`:'—'}</span></td>
                      <td><span className="mono-sm" style={{color:d.earlyMinutes>0?'#ef4444':'#b0b5cc'}}>{d.earlyMinutes>0?`${d.earlyMinutes}m`:'—'}</span></td>
                      <td><span className="amt">{fmt(d.regularPay)}</span></td>
                      <td>{d.overtimePay>0?<span className="amt purple">{fmt(d.overtimePay)}</span>:<span className="amt dim">—</span>}</td>
                      {/* Incentive column */}
                      <td>{(d.incentive||0)>0?<span className="amt teal">{fmt(d.incentive)}</span>:<span className="amt dim">—</span>}</td>
                      <td>{d.deductions>0?<span className="amt red">−{fmt(d.deductions)}</span>:<span className="amt dim">—</span>}</td>
                      <td className="right"><span className="amt green">{fmt(d.netDay)}</span></td>
                      <td><button className="edit-btn" onClick={() => openEdit(d)} title="Edit"><PencilSquareIcon /></button></td>
                    </tr>
                  ))}
                  {/* Total row */}
                  <tr className="total-row">
                    <td colSpan={4}><span style={{fontFamily:'Sora',fontWeight:700,fontSize:'.8rem',color:'#0f1123'}}>Monthly Total</span><div style={{fontSize:'.67rem',color:'#9094a8',marginTop:2}}>{totals.daysPresent} present · {totals.daysAbsent} absent · {totals.daysOnLeave||0} on leave</div></td>
                    <td><span className="mono-sm" style={{fontWeight:700,color:'#0f1123'}}>{totals.totalHours} hrs</span></td>
                    <td>{totals.totalOvertimeHours>0?<span className="ot-badge">+{totals.totalOvertimeHours}</span>:<span className="amt dim">—</span>}</td>
                    <td><span className="mono-sm" style={{color:'#f59e0b',fontWeight:700}}>{totals.totalLateMinutes>0?`${totals.totalLateMinutes}m`:'—'}</span></td>
                    <td><span className="mono-sm" style={{color:'#ef4444',fontWeight:700}}>{totals.totalEarlyMinutes>0?`${totals.totalEarlyMinutes}m`:'—'}</span></td>
                    <td><span className="amt" style={{fontWeight:700}}>{fmt(totals.regularPay)}</span></td>
                    <td>{totals.overtimePay>0?<span className="amt purple" style={{fontWeight:700}}>{fmt(totals.overtimePay)}</span>:<span className="amt dim">—</span>}</td>
                    {/* Incentive total */}
                    <td>{totalIncentives>0?<span className="amt teal" style={{fontWeight:700}}>{fmt(totalIncentives)}</span>:<span className="amt dim">—</span>}</td>
                    <td>{totals.deductions>0?<span className="amt red" style={{fontWeight:700}}>−{fmt(totals.deductions)}</span>:<span className="amt dim">—</span>}</td>
                    <td className="right"><span className="amt green" style={{fontWeight:700,fontSize:'.9rem'}}>{fmt(totals.netSalary)}</span></td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="day-cards">
              {days.map((d,i) => (
                <div key={i} className="day-card">
                  <div className="dc-top">
                    <div style={{display:'flex',alignItems:'center',gap:6,flexWrap:'wrap'}}>
                      <div><div className="dc-date">{fmtDate(d.date)}</div><div className="dc-dow">{dayOfWeek(d.date)}</div></div>
                      <div style={{display:'flex',gap:4,flexWrap:'wrap'}}>
                        <span className={`status-pill ${d.status}`}>{d.status}</span>
                        {d.status==='on-leave'&&d.leaveType&&<span className={`leave-type-pill ${d.leaveType}`}>{d.leaveType}</span>}
                      </div>
                    </div>
                    <button style={{width:26,height:26,borderRadius:7,border:'1px solid #e4e7f0',background:'white',color:'#9094a8',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}} onClick={() => openEdit(d)}><PencilSquareIcon style={{width:13,height:13}}/></button>
                  </div>
                  {d.checkIn&&<div className="dc-row"><span className="dc-label">Check In/Out</span><span className="mono-sm">{fmtTime(d.checkIn)} → {fmtTime(d.checkOut)}</span></div>}
                  <div className="dc-row"><span className="dc-label">Hours</span><span className="mono-sm">{d.workHours?.toFixed(2)}h{d.overtimeHours>0?<> · <span style={{color:'#8b5cf6'}}>+{d.overtimeHours?.toFixed(2)} OT</span></>:''}</span></div>
                  {d.lateMinutes>0   &&<div className="dc-row"><span className="dc-label">Late</span><span style={{color:'#f59e0b',fontFamily:"'JetBrains Mono',monospace",fontSize:'.75rem',fontWeight:600}}>{d.lateMinutes}m</span></div>}
                  {(d.incentive||0)>0&&<div className="dc-row"><span className="dc-label">Incentive</span><span className="amt teal">{fmt(d.incentive)}</span></div>}
                  {d.deductions>0    &&<div className="dc-row"><span className="dc-label">Deduction</span><span className="amt red">−{fmt(d.deductions)}</span></div>}
                  <div className="dc-row"><span className="dc-label">Net Pay</span><span className="dc-net">{fmt(d.netDay)}</span></div>
                </div>
              ))}
              <div className="day-total-card">
                <div style={{fontWeight:700,fontSize:'.82rem',color:'#0f1123',marginBottom:8}}>Monthly Total</div>
                <div className="dtc-row"><span className="dtc-label">Total Hours</span><span className="mono-sm" style={{fontWeight:700}}>{totals.totalHours}h</span></div>
                <div className="dtc-row"><span className="dtc-label">Regular Pay</span><span className="amt">{fmt(totals.regularPay)}</span></div>
                {totals.overtimePay>0 &&<div className="dtc-row"><span className="dtc-label">OT Pay</span><span className="amt purple">{fmt(totals.overtimePay)}</span></div>}
                {totalIncentives>0    &&<div className="dtc-row"><span className="dtc-label">Incentives</span><span className="amt teal">{fmt(totalIncentives)}</span></div>}
                {totals.deductions>0  &&<div className="dtc-row"><span className="dtc-label">Deductions</span><span className="amt red">−{fmt(totals.deductions)}</span></div>}
                <div className="dtc-row" style={{borderTop:'1.5px solid #e8ebf4',marginTop:4,paddingTop:8}}>
                  <span className="dtc-label" style={{color:'#0f1123',fontWeight:700}}>Net Salary</span>
                  <span className="amt green" style={{fontSize:'.9rem',fontWeight:700}}>{fmt(totals.netSalary)}</span>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Add Day Modal */}
      {addOpen && createPortal(
        <div className="modal-backdrop" onClick={() => setAddOpen(false)}>
          <div className="modal modal-lg" onClick={e=>e.stopPropagation()}>
            <div className="modal-head"><div className="modal-title">Add Attendance Record</div><button className="modal-close" onClick={()=>setAddOpen(false)}><XMarkIcon /></button></div>
            <p className="modal-sub">Create a manual attendance record for a day the teacher forgot to check in.</p>
            <div className="edit-grid">
              <div className="edit-field span2"><label className="edit-label">Date</label><input type="date" className="edit-input mono" value={addForm.date} min={`${month}-01`} max={`${month}-31`} onChange={e=>setAddForm({...addForm,date:e.target.value})} /><span className="edit-hint">Must be within {monthLabel(month)}</span></div>
              <div className="edit-field span2"><label className="edit-label">Status</label>
                <select className="edit-select" value={addForm.status} onChange={e=>setAddForm({...addForm,status:e.target.value})}>
                  <option value="present">Present</option><option value="absent">Absent</option>
                </select>
              </div>
              <div className="edit-field"><label className="edit-label">Check In</label><input type="time" className="edit-input mono" value={addForm.checkIn} onChange={e=>setAddForm({...addForm,checkIn:e.target.value})} /></div>
              <div className="edit-field"><label className="edit-label">Check Out</label><input type="time" className="edit-input mono" value={addForm.checkOut} onChange={e=>setAddForm({...addForm,checkOut:e.target.value})} /><span className="edit-hint">Work hours auto-calculated</span></div>
              <div className="edit-field"><label className="edit-label">Late Minutes</label><input type="number" min="0" className="edit-input mono" value={addForm.lateMinutes} onChange={e=>setAddForm({...addForm,lateMinutes:e.target.value})} /></div>
              <div className="edit-field"><label className="edit-label">Early Exit Min</label><input type="number" min="0" className="edit-input mono" value={addForm.earlyExitMinutes} onChange={e=>setAddForm({...addForm,earlyExitMinutes:e.target.value})} /></div>
            </div>
            {addForm.checkIn&&addForm.checkOut&&(()=>{
              const p=previewCalc(addForm,days[0]?.ruleSnapshot);
              return(<div className="preview-box" style={{marginTop:16}}><div className="preview-title">Live Salary Preview</div><div className="preview-row"><span className="preview-key">Work Hours</span><span className="preview-val">{p.workHours.toFixed(2)} hrs</span></div>{p.overtimeHours>0&&<div className="preview-row"><span className="preview-key">Overtime</span><span className="preview-val purple">+{p.overtimeHours.toFixed(2)} hrs</span></div>}{p.deductions>0&&<div className="preview-row"><span className="preview-key">Deductions</span><span className="preview-val red">− {fmt(p.deductions)}</span></div>}<div className="preview-row total"><span className="preview-key">Net for this day</span><span className="preview-val green">{fmt(p.netDay)}</span></div></div>);
            })()}
            <div className="modal-btns">
              <button className="btn btn-ghost" onClick={()=>setAddOpen(false)}>Cancel</button>
              <button className="btn btn-blue" onClick={handleAddSave} disabled={adding}>{adding?'Saving…':'Create Record'}</button>
            </div>
          </div>
        </div>
      
      , document.body)}

      {/* Edit Day Modal — includes Incentive field */}
      {editDay && createPortal(
        <div className="modal-backdrop" onClick={() => setEditDay(null)}>
          <div className="modal modal-lg" onClick={e=>e.stopPropagation()}>
            <div className="modal-head"><div className="modal-title">Edit — {fmtDate(editDay.date)} ({dayOfWeek(editDay.date)})</div><button className="modal-close" onClick={()=>setEditDay(null)}><XMarkIcon /></button></div>
            <div className="edit-grid">
              <div className="edit-field span2"><label className="edit-label">Status</label>
                <select className="edit-select" value={editForm.status} onChange={e=>setEditForm({...editForm,status:e.target.value})}>
                  <option value="present">Present</option><option value="absent">Absent</option>
                </select>
              </div>
              <div className="edit-field"><label className="edit-label">Check In</label><input type="time" className="edit-input mono" value={editForm.checkIn} onChange={e=>setEditForm({...editForm,checkIn:e.target.value})} /><span className="edit-hint">Leave blank to keep unchanged</span></div>
              <div className="edit-field"><label className="edit-label">Check Out</label><input type="time" className="edit-input mono" value={editForm.checkOut} onChange={e=>setEditForm({...editForm,checkOut:e.target.value})} /><span className="edit-hint">Work hours auto-calculated</span></div>
              <div className="edit-field"><label className="edit-label">Late Minutes</label><input type="number" min="0" className="edit-input mono" value={editForm.lateMinutes} onChange={e=>setEditForm({...editForm,lateMinutes:e.target.value})} /></div>
              <div className="edit-field"><label className="edit-label">Early Exit Min</label><input type="number" min="0" className="edit-input mono" value={editForm.earlyExitMinutes} onChange={e=>setEditForm({...editForm,earlyExitMinutes:e.target.value})} /></div>
              {/* Incentive */}
              <div className="edit-field span2">
                <label className="edit-label" style={{color:'#8b5cf6'}}>Incentive / Bonus (Rs.)</label>
                <input type="number" min="0" step="0.01" className="edit-input mono" value={editForm.incentive}
                  onChange={e=>setEditForm({...editForm,incentive:e.target.value})}
                  style={{borderColor:parseFloat(editForm.incentive)>0?'#8b5cf6':undefined}} />
                <span className="edit-hint">One-time bonus or incentive for this day</span>
              </div>
            </div>
            {preview && (
              <div className="preview-box" style={{marginTop:16}}>
                <div className="preview-title">Live Salary Preview</div>
                <div className="preview-row"><span className="preview-key">Work Hours</span><span className="preview-val">{Number(preview.workHours).toFixed(2)} hrs</span></div>
                <div className="preview-row"><span className="preview-key">Regular Hours</span><span className="preview-val">{preview.regularHours.toFixed(2)} hrs</span></div>
                {preview.overtimeHours>0&&<div className="preview-row"><span className="preview-key">Overtime</span><span className="preview-val purple">+{preview.overtimeHours.toFixed(2)} hrs</span></div>}
                <div className="preview-row"><span className="preview-key">Regular Pay</span><span className="preview-val">{fmt(preview.regularPay)}</span></div>
                {preview.overtimePay>0&&<div className="preview-row"><span className="preview-key">OT Pay</span><span className="preview-val purple">{fmt(preview.overtimePay)}</span></div>}
                {parseFloat(editForm.incentive)>0&&<div className="preview-row"><span className="preview-key">Incentive</span><span className="preview-val teal">{fmt(parseFloat(editForm.incentive)||0)}</span></div>}
                {preview.deductions>0&&<div className="preview-row"><span className="preview-key">Deductions</span><span className="preview-val red">− {fmt(preview.deductions)}</span></div>}
                <div className="preview-row total"><span className="preview-key">Net for this day</span><span className="preview-val green">{fmt(preview.netDay+(parseFloat(editForm.incentive)||0))}</span></div>
              </div>
            )}
            <div className="modal-btns">
              <button className="btn btn-ghost" onClick={()=>setEditDay(null)}>Cancel</button>
              <button className="btn btn-blue" onClick={handleEditSave} disabled={saving}>{saving?'Saving…':'Save Changes'}</button>
            </div>
          </div>
        </div>
      
      , document.body)}

      {/* Confirm Modal */}
      {confirm && createPortal(
        <div className="modal-backdrop" onClick={() => setConfirm(null)}>
          <div className="modal modal-sm" onClick={e=>e.stopPropagation()}>
            <div className="modal-head">
              <div className="modal-title">{confirm.type==='finalize'?(isFinalized?'Re-finalize month?':'Finalize month?'):`Mark as ${confirm.newStatus}?`}</div>
              <button className="modal-close" onClick={()=>setConfirm(null)}><XMarkIcon /></button>
            </div>
            <p className="modal-sub">
              {confirm.type==='finalize'
                ? isFinalized?'This will recalculate and overwrite the saved record. Payment status is preserved.':'This saves the current calculation. You can re-finalize anytime.'
                : confirm.newStatus==='paid'?`Marks ${teacher?.name}'s ${monthLabel(month)} salary as paid.`:`Marks ${teacher?.name}'s ${monthLabel(month)} salary as unpaid again.`}
            </p>
            <div className="modal-btns">
              <button className="btn btn-ghost" onClick={()=>setConfirm(null)}>Cancel</button>
              {confirm.type==='finalize'
                ?<button className="btn btn-blue" onClick={handleFinalize} disabled={finalizing}>{finalizing?'Saving…':isFinalized?'Re-finalize':'Finalize'}</button>
                :<button className={`btn ${confirm.newStatus==='paid'?'btn-green':'btn-red'}`} onClick={()=>handleStatusChange(confirm.newStatus)}>Confirm</button>}
            </div>
          </div>
        </div>
      
      , document.body)}
    </div>
  );
};

export default TeacherSalaryMonth;